import { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ArrowRight, Trees, Sparkles, Palette, ChefHat, Flame,
  Waves, Fence, Cctv, House, MapPin, Car, Users, Shapes,
  Snowflake, Wind, Leaf, GraduationCap, PersonStanding, Dumbbell,
  MessagesSquare, Music, Activity, MoveLeft, MoveRight,
} from "lucide-react";
import { ASSETS, PORTALS } from "@/data/content";
import { lockScroll, unlockScroll } from "@/lib/scroll";
import PortalVisual from "@/components/PortalVisual";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { imgProps } from "@/lib/img";

const ICONS = {
  Trees, Sparkles, Palette, ChefHat, Flame, Waves, Fence, Cctv, House,
  Car, MapPin, Users, Shapes, Snowflake, Wind, Leaf, GraduationCap,
  PersonStanding, Dumbbell, MessagesSquare, Music, Activity,
};

const Particles = ({ colors }) => {
  const parts = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        left: 4 + Math.random() * 92,
        bottom: Math.random() * 30,
        size: 3 + Math.random() * 6,
        delay: Math.random() * 0.7,
        dur: 2.4 + Math.random() * 2.4,
        drift: 60 + Math.random() * 160,
        color: colors[i % colors.length],
      })),
    [colors]
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="portal-particles">
      {parts.map((p) => (
        <motion.span
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.left}%`,
            bottom: `${p.bottom}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 2.5}px ${p.color}`,
          }}
          initial={{ opacity: 0, y: 0, scale: 0 }}
          animate={{ opacity: [0, 1, 0], y: -p.drift, scale: [0, 1, 0.4], x: [0, (p.id % 2 ? 1 : -1) * 26] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "easeOut" }}
        />
      ))}
    </div>
  );
};

const FeatureCard = ({ item, glow, testid }) => {
  const Icon = ICONS[item.icon];
  if (item.img || item.placeholder) {
    return (
      <div
        className={`rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition-colors duration-300 ${
          item.wide ? "sm:col-span-2" : ""
        }`}
        data-testid={testid}
      >
        {item.img ? (
          <div className="overflow-hidden">
            <img
              {...imgProps(item.img, item.wide ? "(max-width: 640px) 88vw, 880px" : "(max-width: 640px) 88vw, 440px")}
              alt={item.text}
              className="w-full h-auto block hover:scale-105 transition-transform duration-700"
            />
          </div>
        ) : (
          <div className="h-36 sm:h-44 border-b border-dashed border-amber-300/30 bg-white/[0.02] flex flex-col items-center justify-center gap-2">
            <Icon className="w-7 h-7 text-amber-300/60" />
            <span className="text-[11px] text-amber-100/50 tracking-wide">Фото незабаром</span>
          </div>
        )}
        <div className="flex items-center gap-2.5 px-4 py-3">
          <Icon className="w-4 h-4 text-amber-300 shrink-0" />
          <span className="text-sm font-semibold text-white">{item.text}</span>
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex items-center gap-3 rounded-2xl px-4 py-3.5 border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors duration-300"
      data-testid={testid}
    >
      <span
        className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ background: glow, boxShadow: `0 0 18px ${glow}` }}
      >
        <Icon className="w-5 h-5 text-amber-100" />
      </span>
      <span className="text-sm text-slate-100/95">{item.text}</span>
    </div>
  );
};

const Panel = ({ k, onClose, onEnroll }) => {
  const p = PORTALS[k];
  const logo = k === "park" ? ASSETS.parkLogo : ASSETS.royalLogo;
  return (
    <motion.div
      initial={{ scale: 0.45, opacity: 0, y: 90, borderRadius: "50%" }}
      animate={{ scale: 1, opacity: 1, y: 0, borderRadius: "32px" }}
      exit={{ scale: 0.45, opacity: 0, y: 90, borderRadius: "50%" }}
      transition={{ type: "spring", stiffness: 210, damping: 24 }}
      className="relative z-10 w-[calc(100%-2rem)] sm:w-[94%] max-w-5xl max-h-[84vh] sm:max-h-[86vh] overflow-y-auto overscroll-contain magical-scrollbar"
      style={{
        background: p.panelGradient,
        border: `1px solid ${p.border}`,
        boxShadow: `0 0 90px ${p.glow}, 0 30px 80px rgba(0,0,0,0.6), inset 0 0 70px rgba(255,220,140,0.05)`,
        backdropFilter: "blur(22px)",
        WebkitOverflowScrolling: "touch",
      }}
      data-testid={`panel-${k}`}
      data-lenis-prevent
    >
      <button
        type="button"
        onClick={onClose}
        className="sticky top-4 float-right mr-4 z-20 w-10 h-10 rounded-full glass flex items-center justify-center text-amber-100 hover:text-amber-300 hover:rotate-90 transition-all duration-300 cursor-pointer"
        data-testid="portal-close-button"
        aria-label="Закрити"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row gap-6 md:gap-8 items-stretch"
        >
          <div className="flex-1 flex flex-col justify-center min-w-0">
            <div className="flex items-center gap-3">
              <img src={logo} alt={p.name} className="w-12 h-12 rounded-xl object-cover bg-white shadow-lg" loading="lazy" decoding="async" />
              <span className="font-cinzel text-amber-200 text-sm sm:text-base tracking-[0.15em]">{p.name}</span>
            </div>
            <h3 className="mt-5 font-display text-2xl sm:text-4xl font-bold text-white leading-tight" data-testid={`panel-intro-${k}`}>
              {p.intro}
            </h3>
            {p.areaNote && (
              <p className="mt-3.5 text-amber-100/80 text-sm sm:text-base leading-relaxed max-w-md" data-testid={`panel-area-note-${k}`}>
                {p.areaNote}
              </p>
            )}
          </div>
          <div className="md:w-[42%] shrink-0 rounded-2xl overflow-hidden border border-white/10 shadow-[0_16px_50px_rgba(0,0,0,0.4)] self-start">
            <img
              {...imgProps(p.introImg, "(max-width: 768px) 88vw, 560px")}
              alt={p.name}
              className="w-full h-auto block hover:scale-105 transition-transform duration-700"
              data-testid={`panel-hero-photo-${k}`}
            />
          </div>
        </motion.div>

        {p.groups.map((g, gi) => (
          <motion.div
            key={g.title}
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + gi * 0.12, duration: 0.7 }}
            className="mt-9"
            data-testid={`panel-group-${k}-${gi}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-400/40" />
              <h4 className="font-cinzel text-amber-200 text-sm sm:text-base tracking-[0.2em] uppercase whitespace-nowrap">
                {g.title}
              </h4>
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-400/40" />
            </div>
            {g.subgroups && (
              <div
                className="mb-4 rounded-2xl border border-amber-300/25 bg-amber-300/[0.07] px-4 py-4"
                data-testid={`panel-subgroups-${k}`}
              >
                <div className="text-sm text-amber-100/90 font-medium">{g.subgroups.title}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {g.subgroups.chips.map((c) => (
                    <span
                      key={c}
                      className="rounded-full border border-amber-300/40 bg-white/[0.05] px-4 py-2 text-sm text-amber-100"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {g.teachNote && (
              <p
                className="mb-4 rounded-2xl border border-amber-300/20 bg-white/[0.03] px-4 py-3.5 text-sm sm:text-[15px] text-slate-100/90 leading-relaxed"
                data-testid={`panel-teachnote-${k}-${gi}`}
              >
                <span className="text-amber-300 font-display italic">✦</span> {g.teachNote}
              </p>
            )}
            {(() => {
              const photoItems = g.items.filter((it) => it.img || it.placeholder);
              const iconItems = g.items.filter((it) => !it.img && !it.placeholder);
              return (
                <>
                  <div className="grid sm:grid-cols-2 gap-3 items-start">
                    {photoItems.map((item, ii) => (
                      <FeatureCard key={item.text} item={item} glow={p.glow} testid={`panel-feature-${k}-${gi}-${ii}`} />
                    ))}
                  </div>
                  {iconItems.length > 0 && (
                    <div className="grid sm:grid-cols-2 gap-3 mt-3">
                      {iconItems.map((item, ii) => (
                        <FeatureCard key={item.text} item={item} glow={p.glow} testid={`panel-icon-${k}-${gi}-${ii}`} />
                      ))}
                    </div>
                  )}
                </>
              );
            })()}          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10 pt-6"
        >
          <div className="flex items-center gap-2 text-amber-100/80 text-sm">
            <MapPin className="w-4 h-4 text-amber-300" />
            {p.address}
          </div>
          <button
            type="button"
            onClick={() => onEnroll(k)}
            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-semibold px-8 py-3.5 shadow-[0_0_30px_rgba(230,184,92,0.5)] hover:shadow-[0_0_45px_rgba(230,184,92,0.8)] transition-shadow duration-300 cursor-pointer"
            data-testid={`panel-enroll-button-${k}`}
          >
            Записатися
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default function Kindergartens({ active, setActive, onEnroll }) {
  useEffect(() => {
    if (active) lockScroll();
    else unlockScroll();
    return () => unlockScroll();
  }, [active]);

  return (
    <section id="sadochky" className="relative py-16 sm:py-24 overflow-hidden" data-testid="kindergartens-section">
      <div
        className="absolute inset-0 bg-cover bg-center hidden md:block"
        style={{ backgroundImage: `url(${ASSETS.forestBg})` }}
        data-testid="kindergartens-bg"
      />
      <div
        className="absolute inset-0 bg-cover bg-center md:hidden"
        style={{ backgroundImage: `url(${ASSETS.forestBgMobile})` }}
        data-testid="kindergartens-bg-mobile"
      />
      <div className="absolute inset-0 bg-[#061812]/72" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#061812] via-transparent to-[#061812]" />
      <div className="relative">
      <Reveal>
        <Eyebrow number="02" text="Наші садочки" />
        <h2 className="font-display text-center text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-testid="kindergartens-title">
          ОБЕРІТЬ СВІЙ <span className="text-gold-gradient">КАЗКОВИЙ СВІТ</span>
        </h2>
      </Reveal>

      <Reveal delay={0.15} className="mt-6 hidden md:flex items-center justify-center gap-4 text-amber-100/75 text-sm">
        <MoveLeft className="w-4 h-4 animate-float-soft" />
        <span data-testid="kindergartens-hint">Оберіть садочок, щоб дізнатися більше</span>
        <MoveRight className="w-4 h-4 animate-float-soft" />
      </Reveal>

      <div className="mt-14 flex flex-col md:flex-row items-center justify-center gap-12 md:gap-16 px-4">
        <PortalVisual
          k="park"
          dimmed={active === "royal"}
          onClick={() => setActive("park")}
          testid="portal-park-kids"
        />
        <PortalVisual
          k="royal"
          dimmed={active === "park"}
          onClick={() => setActive("royal")}
          testid="portal-royal-kids"
        />
      </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            key={active}
            className="fixed inset-0 z-[80] flex items-center justify-center p-3"
            data-testid="portal-overlay"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="absolute inset-0 bg-[#030a07]/80 backdrop-blur-md"
              onClick={() => setActive(null)}
              data-testid="portal-backdrop"
            />
            <Particles colors={PORTALS[active].particleColors} />
            <Panel k={active} onClose={() => setActive(null)} onEnroll={onEnroll} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
